<template>
  <ds-dialog
    id="warning-banner-form"
    :title="`${isEdit ? 'Wijzig' : 'Nieuwe'} waarschuwingsbanner`"
    size="md"
    as="form"
    @close="$emit('cancel')"
    @submit.prevent="onSubmit"
  >
    <div class="ds-form">
      <div class="ds-form__row">
        <div class="ds-form__field">
          <label for="brandCode">Merk *</label>
          <select id="brandCode" v-model="form.BrandCode" required class="ds-field">
            <option v-for="brand in brandOptions" :key="brand.value" :value="brand.value">
              {{ brand.label }}
            </option>
          </select>
        </div>

        <div class="ds-form__field">
          <label for="backgroundColor">Kleur *</label>
          <select id="backgroundColor" v-model="form.BackgroundColor" required class="ds-field">
            <option value="red">Rood</option>
            <option value="dimgray">Licht grijs</option>
            <option value="orange">Oranje</option>
          </select>
        </div>
      </div>

      <div class="ds-form__field">
        <label for="textBlock">Tekst (HTML toegestaan) *</label>
        <textarea
          id="textBlock"
          v-model="form.TextBlock"
          rows="4"
          required
          class="ds-field"
          placeholder='Bijv. Gepland onderhoud op zondag. &lt;a href="#"&gt;Meer info&lt;/a&gt;'
        ></textarea>
      </div>

      <div v-if="form.TextBlock">
        <div class="warning-banner-form__preview-label">Voorbeeld</div>
        <div
          class="warning-banner-form__preview"
          :style="{ backgroundColor: form.BackgroundColor }"
          v-html="form.TextBlock"
        ></div>
      </div>

      <div class="ds-form__row">
        <div class="ds-form__field">
          <label for="dateFrom">Begindatum *</label>
          <input
            id="dateFrom"
            v-model="form.DateFrom"
            type="datetime-local"
            required
            class="ds-field"
          />
        </div>

        <div class="ds-form__field">
          <label for="dateTo">Einddatum</label>
          <input id="dateTo" v-model="form.DateTo" type="datetime-local" class="ds-field" />
          <span class="ds-form__hint">Leeg laten als er geen einddatum is.</span>
        </div>
      </div>

      <div v-if="error" class="ds-alert ds-alert--danger">{{ error }}</div>
    </div>

    <template #footer>
      <ds-button
        variant="text"
        type="button"
        data-qa="warning-banner-form-cancel"
        @click="$emit('cancel')"
      >
        Annuleren
      </ds-button>
      <ds-button type="submit" data-qa="warning-banner-form-submit" :disabled="loading">
        <ds-spinner v-if="loading" inline />
        <ds-icon v-else-if="isEdit" name="edit" />
        <ds-icon v-else name="save" />
        {{ loading ? 'Opslaan…' : isEdit ? 'Wijzig' : 'Opslaan' }}
      </ds-button>
    </template>
  </ds-dialog>
</template>

<script>
import warningBannerService from '@/services/warningBannerService'
import moment from 'moment'
import { mapState } from 'vuex'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'

const BRAND_MAP = {
  '*': 'Alle merken',
  V: 'Volkswagen',
  N: 'Volkswagen bedrijfswagens',
  S: 'Seat',
  C: 'Skoda',
  A: 'Audi',
}

export default {
  name: 'WarningBannerForm',
  components: { DsDialog, DsButton, DsIcon, DsSpinner },
  props: {
    initial: {
      type: Object,
      default: null,
    },
  },
  emits: ['cancel', 'saved'],
  data() {
    return {
      form: {
        Id: null,
        BrandCode: '*',
        BackgroundColor: 'red',
        TextBlock: '',
        DateFrom: '',
        DateTo: '',
      },
      loading: false,
      error: null,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    brandOptions: function () {
      return Object.entries(BRAND_MAP)
        .filter(([value]) => value === this.dealerSettings.brand || value === '*')
        .map(([value, label]) => ({ value, label }))
    },
    isEdit() {
      return this.initial && this.initial.Id
    },
  },
  watch: {
    initial: {
      handler() {
        this.initializeForm()
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeForm()
  },
  methods: {
    initializeForm() {
      if (this.initial) {
        this.form = {
          Id: this.initial.Id,
          BrandCode: this.initial.BrandCode || '*',
          BackgroundColor: this.initial.BackgroundColor || 'red',
          TextBlock: this.initial.TextBlock || '',
          DateFrom: this.initial.DateFrom ? this.initial.DateFrom.slice(0, 16) : '',
          DateTo: this.initial.DateTo ? this.initial.DateTo.slice(0, 16) : '',
        }
      } else {
        // Set default start time to now, local date time
        const localDateTime = moment().format('YYYY-MM-DDTHH:mm:ss.SSS')
        this.form.DateFrom = localDateTime.slice(0, 16)
      }
    },
    async onSubmit() {
      this.error = null
      this.loading = true

      try {
        // Convert local datetime to ISO string for API
        const warningBanner = {
          BrandCode: this.form.BrandCode,
          BackgroundColor: this.form.BackgroundColor,
          TextBlock: this.form.TextBlock.trim(),
          DateFrom: this.form.DateFrom,
          DateTo: this.form.DateTo,
        }

        // Add ID for updates
        if (this.isEdit) {
          warningBanner.Id = this.form.Id
        }
        const apiCall = this.isEdit
          ? warningBannerService.updateWarningBanner(this.form.Id, warningBanner)
          : warningBannerService.createNewWarningBanner(warningBanner)

        await apiCall
        this.$emit('saved')
      } catch (error) {
        console.error('Save error:', error)
        this.error =
          error.response?.data?.message ||
          error.message ||
          'Opslaan van de banner is mislukt. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only — the dialog, form grid, fields, buttons and error banner all
// come from the design system (styles/ds/).

.warning-banner-form__preview-label {
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ds-color-text-subtle);
}

// The banner colours are admin-chosen and always dark, so the preview text is
// white with a shadow to stay legible on any of them — same as the live banner.
.warning-banner-form__preview {
  margin-top: 4px;
  padding: 14px var(--ds-space-4);
  border-radius: var(--ds-radius-md);
  color: #fff;
  font-size: var(--ds-font-size-sm);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);

  :deep(a) {
    color: #fff;
    text-decoration: underline;
  }
}
</style>
